import { type ActorRefFrom, setup } from 'xstate5';

import {
  type AzureAuthStateMachineContext,
  type AzureAuthStateMachineEvents,
} from './azure-auth-machine.types';
import { Actions } from './setup/azure-auth-machine.actions';
import { Actors } from './setup/azure-auth-machine.actors';
import { Guards } from './setup/azure-auth-machine.guards';

// ─────────────────────────────────────────────────────────────────────────────

const initialContext: AzureAuthStateMachineContext = {
  email: undefined,
  apiAccessToken: undefined,
  apiRefreshToken: undefined,
  createAccountInput: undefined,
};

const model = setup({
  types: {
    context: {} as AzureAuthStateMachineContext,
    events: {} as AzureAuthStateMachineEvents,
  },
  actors: Actors,
  actions: Actions,
  guards: Guards,
});

/**
 * A state machine that handles the various steps of the authentication process
 * by initiating multiple flows through internal services.
 */
export const azureAuthMachine = model.createMachine({
  context: initialContext,
  initial: 'waiting',
  states: {
    waiting: {
      description: 'An idle state, where the machine is ready to start.',
      on: {
        INITIALIZE: 'initializing',
      },
    },
    initializing: {
      description:
        'A state in which the machine is setting up any necessary operations before being able to handle authentication flows.',
      invoke: {
        id: 'initialize',
        src: 'initialize',
        onDone: [
          {
            target: 'handling-redirect',
            guard: 'checkIfNeedToHandleRedirect',
          },
          { target: 'authenticating' },
        ],
        onError: 'waiting',
      },
      on: {
        RESET: 'waiting',
      },
    },
    restarting: {
      entry: ['removeRefreshToken'],
      description: 'A state in which the machine is restarting the auth flow.',
      always: 'authenticating',
    },
    'handling-redirect': {
      description: 'Web-only state, in which the machine handles authentication after redirection.',
      invoke: {
        id: 'handleRedirect',
        src: 'handleRedirect',
        onDone: [
          {
            target: 'authenticating',
            guard: 'checkIfSignedOut',
          },
          {
            target: 'signing-in',
            actions: {
              type: 'storeAccountData',
              params: ({ event }) => ({ accountData: event.output }),
            },
          },
        ],
        onError: {
          target: 'ad-authentication-failed',
          actions: {
            type: 'onSystemError',
            params: ({ event }) => ({ systemError: event.error }),
          },
        },
      },
    },
    authenticating: {
      description:
        'A state where an authentication request is initiated. The web platform will utilize a redirect flow, while native will use a popup flow.',
      invoke: {
        id: 'authenticate',
        src: 'authenticate',
        onDone: {
          target: 'signing-in',
          actions: {
            type: 'storeAccountData',
            params: ({ event }) => ({ accountData: event.output }),
          },
        },
        onError: {
          target: 'ad-authentication-failed',
          actions: {
            type: 'onSystemError',
            params: ({ event }) => ({ systemError: event.error }),
          },
        },
      },
      on: {
        RESET: 'waiting',
      },
    },
    'ad-authentication-failed': {
      description:
        'A status that signals a failed or interrupted authentication request while interacting with Azure AD.',
      entry: ['removeActiveInteractionState'],
      on: {
        RESET: 'waiting',
        RESTART: 'restarting',
      },
    },
    'authentication-failed': {
      description:
        'A status that signals a failed or interrupted authentication request while interacting with SG services.',
      on: {
        RESET: 'waiting',
        RESTART: 'restarting',
      },
    },
    'signing-in': {
      description:
        'A state in which the machine attempts to authenticate a user to determine whether they already exist. Otherwise, the user will be redirected to the new account creation step.',
      invoke: {
        id: 'signIn',
        src: 'signIn',
        input: ({ context }) => ({ apiAccessToken: context.apiAccessToken }),
        onDone: [
          {
            target: 'redirect',
            guard: { type: 'checkIfSignedIn', params: ({ event }) => event.output },
          },
          { target: 'filling-account-data' },
        ],
        onError: {
          target: 'authentication-failed',
          actions: {
            type: 'onSystemError',
            params: ({ event }) => ({ systemError: event.error }),
          },
        },
      },
    },
    'filling-account-data': {
      description: 'New account creation state in which the user enters needed information.',
      on: {
        SUBMIT_ACCOUNT_DATA: [
          {
            guard: 'checkIfNeedToRefreshTokens',
            target: 'refreshing-access-tokens',
            actions: {
              type: 'storeAccountInput',
              params: ({ event }) => ({ createAccountInput: event.input }),
            },
          },
          {
            target: 'creating-account',
            actions: {
              type: 'storeAccountInput',
              params: ({ event }) => ({ createAccountInput: event.input }),
            },
          },
        ],
        RESET: 'waiting',
        RESTART: 'restarting',
      },
    },
    'refreshing-access-tokens': {
      description:
        'A state in which a machine attempts to refresh access tokens using the stored refresh token.',
      invoke: {
        id: 'refreshAccessTokens',
        src: 'refreshAccessTokens',
        input: ({ context }) => ({ apiRefreshToken: context.apiRefreshToken }),
        onDone: {
          target: 'creating-account',
          actions: {
            type: 'storeAccessTokens',
            params: ({ event }) => ({ accessTokens: event.output }),
          },
        },
        onError: 'filling-account-data',
      },
    },
    'creating-account': {
      description: 'A state in which a machine attempts to create a new account.',
      invoke: {
        id: 'createAccount',
        src: 'createAccount',
        input: ({ context }) => ({
          email: context.email,
          apiAccessToken: context.apiAccessToken,
          createAccountInput: context.createAccountInput,
        }),
        onDone: [
          {
            target: 'redirect',
            guard: { type: 'checkIfSignedUp', params: ({ event }) => event.output },
          },
          { target: 'filling-account-data' },
        ],
        onError: 'filling-account-data',
      },
    },
    redirect: {
      entry: ['storeTokens'],
      description: 'A state that can be utilized to redirect the user following authentication.',
      on: {
        COMPLETE: 'waiting',
        RESTART: 'restarting',
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type AzureAuthMachineActorRef = ActorRefFrom<typeof azureAuthMachine>;
